import { useCallback, useEffect, useRef, useState } from "react";
import { useBackState, useScrollMemory } from "@/hooks/useScrollMemory";
import { useLocation, useNavigationType } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ChecklistTable } from "@/components/catchup/ChecklistTable";
import { MarkGapDialog } from "@/components/catchup/MarkGapDialog";
import { WeekCompleted } from "@/components/catchup/WeekCompleted";
import { SalesNightNote } from "@/components/sales/SalesNightNote";
import { ListChecks, Loader2, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const PAGE = 50;

// A mark saved or taken off changes this one row and the counts — patched in place so the row doesn't jump
// pages under his finger; the order catches up on the next Recount.
function patched(data, { row, item }, next) {
  const was = row.cells[item].state === "gap";
  const now = next.state === "gap";
  const delta = (now ? 1 : 0) - (was ? 1 : 0);
  const rows = data.rows.map((r) => (r.id !== row.id ? r : {
    ...r, missing: r.missing + delta, complete: r.missing + delta === 0, cells: { ...r.cells, [item]: next } }));
  const becameComplete = row.missing + delta === 0 && row.missing !== 0;
  const leftComplete = row.missing === 0 && delta > 0;
  const mDelta = (next.state === "marked" ? 1 : 0) - (row.cells[item].state === "marked" ? 1 : 0);
  const marked = data.marked && { ...data.marked, [item]: (data.marked[item] || 0) + mDelta };
  return { ...data, rows, marked, gaps: { ...data.gaps, [item]: data.gaps[item] + delta },
           complete: data.complete + (becameComplete ? 1 : 0) - (leftComplete ? 1 : 0) };
}

const Definition = ({ defn, kind }) => (
  <ol data-testid="checklist-definition" className="px-3 pb-3 space-y-1.5 text-[13px] text-slate-700 list-decimal list-inside">
    {defn[kind].map((it) => <li key={it.key}><b>{it.label}</b> — {it.rule}</li>)}
    <li className="list-none text-[12px] text-slate-500 pt-1">Who counts: {defn.who_counts[kind]}</li>
  </ol>
);

const Chip = ({ testid, active, onClick, children }) => (
  <button data-testid={testid} onClick={onClick} aria-pressed={!!active}
    className={`text-xs font-semibold rounded-full border px-2.5 py-1.5 transition-colors ${active ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "bg-white border-slate-300 text-slate-700 hover:border-[#B01E28]"}`}>
    {children}
  </button>
);

/* WHAT'S MISSING — one row per truck or part, a tick or a gap per item, most missing first (his list, 22 Oct).
   The items and their meaning come from the server's one definition of "complete" (completeness.py). */
export function WhatsMissingPanel() {
  const location = useLocation();
  const navType = useNavigationType();
  // a progress-panel line opens this list on its own gap (/catch-up?wm=trucks.vin_decoded) — the same count, the same rows
  const wm = new URLSearchParams(location.search).get("wm") || "";
  const [wmKind, wmGap] = wm.split(".");
  const fromLink = navType !== "POP" && (wmKind === "trucks" || wmKind === "parts");
  const [defn, setDefn] = useState(null);
  const [kind, setKind] = useBackState("wm.kind", fromLink ? wmKind : "trucks");
  const [missing, setMissing] = useBackState("wm.missing", fromLink ? wmGap || "" : "");
  const [page, setPage] = useBackState("wm.page", 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDef, setShowDef] = useState(false);
  const [marking, setMarking] = useState(null);
  const [error, setError] = useState("");
  const [stamp, setStamp] = useState(0);

  const asked = useRef(0);
  const load = useCallback(async () => {
    const n = ++asked.current;   // only the newest answer is shown — never one tab's rows under the other's items
    setLoading(true); setError("");
    try {
      const [{ data: df }, { data: d }] = await Promise.all([
        defn ? Promise.resolve({ data: defn }) : api.get("/catch-up/checklist/definition"),
        api.get(`/catch-up/checklist/${kind}`, { params: { missing: missing || undefined, page, page_size: PAGE } })]);
      if (n !== asked.current) return;
      setDefn(df); setData(d);
    } catch (e) { if (n === asked.current) setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not read the list"); }
    if (n === asked.current) setLoading(false);
  }, [kind, missing, page]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  // Inventory health's "Complete" tile lands here (/catch-up#whats-missing). The panels above fill in after this
  // one, so the scroll is re-applied while the page settles — and stops the moment he scrolls himself.
  const ref = useRef(null);
  const landed = useRef(false);
  useScrollMemory(!!data && !loading);
  useEffect(() => {
    // tapped again from the progress panel above, already on this page: switch to that gap and land here again
    if (!fromLink) return;
    if (wmKind !== kind) setData(null);
    setKind(wmKind); setMissing(wmGap || ""); setPage(1);
    landed.current = false;
  }, [location.key]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // on Back he goes to the row he was on, not the top of this panel
    if (!data || landed.current || navType === "POP" || window.location.hash !== "#whats-missing") return undefined;
    landed.current = true;
    const go = () => ref.current?.scrollIntoView({ block: "start" });
    const ro = new ResizeObserver(go);
    const stop = () => { ro.disconnect(); window.removeEventListener("wheel", stop); window.removeEventListener("touchstart", stop); };
    go();
    ro.observe(document.body);
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    const t = setTimeout(stop, 5000);
    return () => { clearTimeout(t); stop(); };
  }, [data, navType]);

  const pick = (k) => { if (k === kind) return; setKind(k); setMissing(""); setPage(1); setData(null); };
  const filter = (k) => { setMissing(k); setPage(1); };
  const items = defn?.[kind] || [];
  const from = data ? (page - 1) * PAGE + 1 : 0;

  return (
    <section ref={ref} id="whats-missing" data-testid="whats-missing-panel" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden scroll-mt-4">
      <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ListChecks className="w-5 h-5 shrink-0" />
          <h2 className="font-display text-base sm:text-lg font-bold leading-tight">What's missing — truck by truck, part by part</h2>
        </div>
        <button data-testid="checklist-refresh" onClick={() => { load(); setStamp((s) => s + 1); }} className="text-[10px] font-mono uppercase tracking-widest bg-white/15 hover:bg-white/25 px-2 py-1 rounded shrink-0">
          <RefreshCw className="w-3 h-3 inline mr-1" />Recount
        </button>
      </div>
      <div className="p-3 sm:p-4 space-y-3">
        <SalesNightNote />
        <WeekCompleted stamp={stamp} />
        <div className="flex gap-2" role="tablist">
          {["trucks", "parts"].map((k) => (
            <button key={k} role="tab" aria-selected={kind === k} data-testid={`checklist-tab-${k}`} onClick={() => pick(k)}
              className={`h-11 px-4 rounded-lg font-bold text-sm border-2 transition-colors ${kind === k ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
              {k === "trucks" ? "Trucks" : "Parts"}
            </button>
          ))}
        </div>
        {data && (
          <div data-testid="checklist-summary-box" className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-2">
            <p data-testid="checklist-summary" className="text-sm text-slate-700">
              <b className="text-emerald-700">{data.complete.toLocaleString()}</b> of {data.total.toLocaleString()} {kind} complete ·
              {" "}<b className="text-[#B01E28]">{(data.total - data.complete).toLocaleString()}</b> with something missing. Reading this changes nothing.
            </p>
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">How many have each gap — tap one to list them</p>
            <div className="flex flex-wrap gap-1.5" data-testid="checklist-filters">
              <Chip testid="checklist-filter-all" active={!missing} onClick={() => filter("")}>Everything</Chip>
              {items.map((it) => (
                <Chip key={it.key} testid={`checklist-filter-${it.key}`} active={missing === it.key} onClick={() => filter(it.key)}>
                  {it.gap} <b className={missing === it.key ? "" : "text-[#B01E28]"}>{(data.gaps[it.key] || 0).toLocaleString()}</b>
                </Chip>
              ))}
            </div>
          </div>
        )}
        {defn && (
          <div className="rounded-lg border border-slate-200">
            <button data-testid="checklist-toggle-definition" onClick={() => setShowDef((v) => !v)}
              className="w-full flex items-center gap-1.5 px-3 py-2.5 text-left font-bold text-sm">
              {showDef ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}What "complete" means for a {kind === "trucks" ? "truck" : "part"}
            </button>
            {showDef && <Definition defn={defn} kind={kind} />}
          </div>
        )}
        {error ? (
          <div data-testid="checklist-error" className="rounded-lg border-2 border-red-200 bg-red-50 p-3 text-sm text-[#B01E28] flex flex-wrap items-center gap-2">
            {error}
            <button data-testid="checklist-retry" onClick={load} className="h-9 px-3 rounded-lg border border-[#B01E28] font-bold">Try again</button>
          </div>
        ) : !data || !defn ? (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-3"><Loader2 className="w-4 h-4 animate-spin" />Reading every {kind === "trucks" ? "truck" : "part"}…</div>
        ) : (
          <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
            <ChecklistTable kind={kind} items={items} rows={data.rows} onMark={setMarking} />
            <div className="flex items-center justify-between gap-2 mt-2 text-[12px] text-slate-500">
              <span data-testid="checklist-showing">{data.listed ? `Showing ${from}–${from + data.rows.length - 1} of ${data.listed.toLocaleString()}` : "None"} · most missing first</span>
              <span className="flex gap-1.5">
                <button data-testid="checklist-prev" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="h-9 px-3 rounded-lg border border-slate-300 font-bold disabled:opacity-40">Prev</button>
                <button data-testid="checklist-next" disabled={!data.has_more} onClick={() => setPage((p) => p + 1)} className="h-9 px-3 rounded-lg border border-slate-300 font-bold disabled:opacity-40">Next</button>
              </span>
            </div>
          </div>
        )}
      </div>
      <MarkGapDialog target={marking} onClose={() => setMarking(null)}
        onDone={(next) => { setData((d) => patched(d, marking, next)); setMarking(null); setStamp((s) => s + 1); }} />
    </section>
  );
}
